"use client";

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { getUsageStats } from "@/app/(authenticated)/settings/_modules/services/subscription-plan-services";

/**
 * Trial dial-time allowance (20 hours of talk time for the whole trial).
 * Reads the same usage-stats the Settings → Subscription bar uses, on the
 * same query key, so the two never disagree. Refreshes every minute and on
 * tab focus; the backend recomputes from call_histories on every request.
 */
export interface TrialDialTime {
  isTrial: boolean;
  usedHours: number;
  limitHours: number;
  remainingHours: number;
  /** 0–100, how much of the allowance is gone */
  usedPct: number;
  /** ≤ 20% or ≤ 2h left */
  nearLimit: boolean;
  /** ≤ 10% or ≤ 1h left */
  almostOut: boolean;
  exhausted: boolean;
  isLoading: boolean;
}

export function useTrialDialTime(): TrialDialTime {
  const { user } = useUserContext();
  const u: any = user?.user || {};
  const isTrial = u.accountType === "trial";

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["subscription", "usage-stats"],
    queryFn: getUsageStats,
    enabled: isTrial && !!u.id,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: 1,
  });

  // The moment a call ends, refetch — twice, because the call's duration is
  // written by the provider's status callback a few seconds after hang-up.
  useEffect(() => {
    if (!isTrial) return;
    const onEnded = () => {
      const t1 = setTimeout(() => { void refetch(); }, 4_000);
      const t2 = setTimeout(() => { void refetch(); }, 20_000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    };
    // Only the latest pair matters; multi-line ends the agent's leg every batch, so keeping
    // every pair grew by two refetches per batch.
    let cancelPending: (() => void) | null = null;
    const handler = () => { cancelPending?.(); cancelPending = onEnded(); };
    window.addEventListener("ic-call-ended", handler);
    return () => {
      window.removeEventListener("ic-call-ended", handler);
      cancelPending?.();
    };
  }, [isTrial, refetch]);

  const dt: any = (data as any)?.dialTime;
  const limitRaw = dt?.limitHours;
  const limitHours = typeof limitRaw === "number" && isFinite(limitRaw) ? limitRaw : Infinity;
  const usedHours = typeof dt?.usedHours === "number" ? dt.usedHours : 0;
  const remainingHours = isFinite(limitHours) ? Math.max(0, limitHours - usedHours) : Infinity;
  const usedPct = isFinite(limitHours) && limitHours > 0 ? Math.min(100, (usedHours / limitHours) * 100) : 0;
  const capped = isTrial && isFinite(limitHours);

  return {
    isTrial: capped,
    usedHours,
    limitHours,
    remainingHours,
    usedPct,
    nearLimit: capped && !isLoading && (remainingHours <= 2 || usedPct >= 80),
    almostOut: capped && !isLoading && (remainingHours <= 1 || usedPct >= 90),
    exhausted: capped && !isLoading && remainingHours <= 0,
    isLoading: capped && isLoading,
  };
}
